import type { AdvisedProduct, Integration, UnionOfType } from "../../../../app/types"
import type { Product } from "../../types"
import type {
  AdvisorInfo,
  SessionContext,
  UsageAnalyticsEventTransformer,
} from "../types"

type GoogleAnalyticsBase<T> = {
  type: T
  integration: "aiden:google_analytics"
  advisorId: string
  advisorName: string
}

type GoogleAnalyticsAnsweredEvent = UnionOfType<
  GoogleAnalyticsBase<"aiden_answered_question"> & {
    eventData: {
      aiden_app_id: string
      aiden_app_name: string
      aiden_question: string
    }
  }
>

type GoogleAnalyticsAnswerGivenEvent = UnionOfType<
  GoogleAnalyticsBase<"aiden_answer_given"> & {
  eventData: {
    aiden_app_id: string
    aiden_app_name: string
    aiden_question: string
    aiden_answer: string
  }
}
>

type GoogleAnalyticsAdvisedEvent = UnionOfType<
  GoogleAnalyticsBase<"aiden_advised"> & {
    eventData: {
      aiden_app_id: string
      aiden_app_name: string
      advice: { id: string; name: string; image: string; link: string }[]
    }
  }
>

type GoogleAnalyticsProductAdvisedEvent = UnionOfType<
  GoogleAnalyticsBase<"aiden_product_advised"> & {
    eventData: {
      aiden_app_id: string
      aiden_app_name: string
      aiden_product_id: string
      aiden_product: string
      aiden_product_image: string
      aiden_product_link: string
    }
  }
>

type GoogleAnalyticsAdvicePageCustomerInfoEvent = UnionOfType<
  GoogleAnalyticsBase<"aiden_advice_page_customer_info_form"> & {
    eventData: {
      aiden_app_id: string
      aiden_app_name: string
      checkboxChecked: boolean
      checkboxContext: string
      advice: { id: string; name: string; image: string; link: string }[]
    }
  }
>

type GoogleAnalyticsNoResultsPageCustomerInfoEvent = UnionOfType<
  GoogleAnalyticsBase<"aiden_no_results_page_customer_info_form"> & {
    eventData: {
      aiden_app_id: string
      aiden_app_name: string
      checkboxChecked: boolean
      checkboxContext: string
    }
  }
>

type GoogleAnalyticsInfoPageCustomerInfoEvent = UnionOfType<
  GoogleAnalyticsBase<"aiden_info_page_customer_info_form"> & {
    eventData: {
      aiden_app_id: string
      aiden_app_name: string
      checkboxChecked: boolean
      checkboxContext: string
      formIdentifier: string
    }
  }
>

type GoogleAnalyticsProductClickedEvent = UnionOfType<
  GoogleAnalyticsBase<"aiden_product_clicked"> & {
    eventData: {
      aiden_app_id: string
      aiden_app_name: string
      aiden_product_id: string
      aiden_product: string
      aiden_product_link: string
    }
  }
>

type GoogleAnalyticsAddToCartEvent = UnionOfType<
  GoogleAnalyticsBase<"aiden_add_to_cart"> & {
  eventData: {
    aiden_app_id: string
    aiden_app_name: string
    advice: { id: string; name: string; quantity: number }[]
  }
}
>

type GoogleAnalyticsProductAddToCartEvent = UnionOfType<
  GoogleAnalyticsBase<"aiden_product_add_to_cart"> & {
  eventData: {
    aiden_app_id: string
    aiden_app_name: string
    aiden_product_id: string
    aiden_product: string
    aiden_product_quantity: number
  }
}
>

type GoogleAnalyticsConversationStartedEvent = UnionOfType<
  GoogleAnalyticsBase<"aiden_conversation_started"> & {
  eventData: {
    aiden_app_id: string
    aiden_app_name: string
  }
}
>


export const GoogleAnalyticsUsageEvents: UsageAnalyticsEventTransformer = {
  questionAnswered: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    question: { title: string; label: string },
    answers: string[]
  ): (GoogleAnalyticsAnsweredEvent | GoogleAnalyticsAnswerGivenEvent)[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integration: "aiden:google_analytics",
        type: "aiden_answered_question",
        eventData: {
          aiden_app_id: advisor.id,
          aiden_app_name: advisor.name,
          aiden_question: question.title,
        },
      },
      ...answers.map((answer) => {
        const event: GoogleAnalyticsAnswerGivenEvent = {
          advisorId: advisor.id,
          advisorName: advisor.name,
          integration: "aiden:google_analytics",
          type: "aiden_answer_given",
          eventData: {
            aiden_app_id: advisor.id,
            aiden_app_name: advisor.name,
            aiden_question: question.title,
            aiden_answer: answer,
          },
        }
        return event
      }),
    ]
  },

  adviceGiven: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    products: Product[]
  ): (GoogleAnalyticsAdvisedEvent | GoogleAnalyticsProductAdvisedEvent)[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integration: "aiden:google_analytics",
        type: "aiden_advised",
        eventData: {
          aiden_app_id: advisor.id,
          aiden_app_name: advisor.name,
          advice: products.map((p) => ({
            id: p.clientProductId,
            name: p.name,
            image: p.image,
            link: p.link,
          })),
        },
      },
      ...products.map((product) => {
        const event: GoogleAnalyticsProductAdvisedEvent = {
          advisorId: advisor.id,
          advisorName: advisor.name,
          integration: "aiden:google_analytics",
          type: "aiden_product_advised",
          eventData: {
            aiden_app_id: advisor.id,
            aiden_app_name: advisor.name,
            aiden_product_id: product.clientProductId,
            aiden_product: product.name,
            aiden_product_image: product.image,
            aiden_product_link: product.link,
          },
        }
        return event
      }),
    ]
  },

  clickedOnProduct: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    product: Product
  ): GoogleAnalyticsProductClickedEvent[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integration: "aiden:google_analytics",
        type: "aiden_product_clicked",
        eventData: {
          aiden_app_id: advisor.id,
          aiden_app_name: advisor.name,
          aiden_product_id: product.clientProductId,
          aiden_product: product.name,
          aiden_product_link: product.link,
        },
      },
    ]
  },
  customerInfoSubmitted: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    isPreview: boolean,
    info: {
      email: string
      checkboxChecked: boolean
      checkboxText: string
      advisedProducts: AdvisedProduct[]
    }
  ): GoogleAnalyticsAdvicePageCustomerInfoEvent[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integration: "aiden:google_analytics",
        type: "aiden_advice_page_customer_info_form",
        eventData: {
          aiden_app_id: advisor.id,
          aiden_app_name: advisor.name,
          checkboxChecked: info.checkboxChecked,
          checkboxContext: info.checkboxText,
          advice: info.advisedProducts.map((p) => ({
            id: p.id,
            name: p.name,
            image: p.imageUrl,
            link: p.url,
          })),
        },
      },
    ]
  },
  infoPageCustomerInfoSubmitted: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    isPreview: boolean,
    info: {
      email: string
      checkboxChecked: boolean
      checkboxText: string
      formIdentifier: string
    }
  ): GoogleAnalyticsInfoPageCustomerInfoEvent[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integration: "aiden:google_analytics",
        type: "aiden_info_page_customer_info_form",
        eventData: {
          aiden_app_id: advisor.id,
          aiden_app_name: advisor.name,
          checkboxChecked: info.checkboxChecked,
          checkboxContext: info.checkboxText,
          formIdentifier: info.formIdentifier,
        },
      },
    ]
  },
  noResultsCustomerInfoSubmitted: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    isPreview: boolean,
    info: {
      email: string
      checkboxChecked: boolean
      checkboxText: string
    }
  ): GoogleAnalyticsNoResultsPageCustomerInfoEvent[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integration: "aiden:google_analytics",
        type: "aiden_no_results_page_customer_info_form",
        eventData: {
          aiden_app_id: advisor.id,
          aiden_app_name: advisor.name,
          checkboxChecked: info.checkboxChecked,
          checkboxContext: info.checkboxText,
        },
      },
    ]
  },


  conversationStarted: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
  ): GoogleAnalyticsConversationStartedEvent[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integration: "aiden:google_analytics",
        type: "aiden_conversation_started",
        eventData: {
          aiden_app_id: advisor.id,
          aiden_app_name: advisor.name,
        },
      },
    ]
  },
}
